import {
  AscendingIcon,
  AwarenessIcon,
  DescendingIcon,
  PhishingIcon,
  ProductType,
  SecurityCultureIcon,
} from '@/utils/helpers';
import { Divider } from 'primereact/divider';
import { Skeleton } from 'primereact/skeleton';
import React, { ReactNode } from 'react';
import styled from 'styled-components';
import FlexContainer from './FlexContainer';

const StyledCardReports = styled.div<{ type?: ProductType }>`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  min-width: min-content;

  .logo-container {
    justify-content: center;
    align-items: center;
    width: 38px;
    height: 38px;
    min-width: 38px;
    min-height: 38px;
    border-radius: var(--xsmall-border-radius);
    background-color: ${({ type }) =>
      type === ProductType.AWARENESS
        ? 'var(--blue-light)'
        : type === ProductType.PHISHING
        ? 'var(--green-chart-pale)'
        : type === ProductType.SECURITY_CULTURE
        ? 'var(--purple-pale)'
        : null};
    svg {
      color: ${({ type }) =>
        type === ProductType.AWARENESS
          ? 'var(--blue-dark)'
          : type === ProductType.PHISHING
          ? 'var(--green-chart-middle)'
          : type === ProductType.SECURITY_CULTURE
          ? 'var(--purple-text)'
          : null};
    }
    margin-right: var(--small-padding);
  }

  .reports-card-title {
    font-weight: ${({ type }) => (type ? 400 : 500)};
    font-size: var(--small-font-size);
    line-height: var(--small-line-height);
    text-transform: ${({ type }) => (type ? 'capitalize' : 'uppercase')};
    margin: 0;
  }

  .reports-card-count {
    font-weight: 700;
    font-size: ${({ type }) =>
      type
        ? 'var(--card-value-font-size)'
        : 'calc(2 * var(--medium-font-size))'};
    line-height: ${({ type }) =>
      type
        ? 'var(--card-value-line-height)'
        : 'calc(2 * var(--xsmall-line-height))'};
    margin: var(--small-padding) 0;
    &.left {
      align-self: flex-start;
    }
  }

  .reports-card-description {
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
    font-weight: 500;
  }

  .reports-card-footer {
    align-self: flex-start;
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
    font-weight: 500;
    margin-top: var(--small-padding);
  }
  .p-divider {
    max-width: 211px;
    align-self: flex-start;
    padding: 0;
    margin: 0;
  }
`;

type CardReportsProps = {
  title: string;
  count: number | undefined;
  isLoading: boolean;
  isPercentage?: boolean;
  type?: ProductType;
  description?: string | ReactNode | undefined;
  footer?: string;
  isLow?: boolean;
  id?: string;
  onClick?: () => void;
};

const CardReports: React.FC<CardReportsProps> = ({
  title,
  count,
  isLoading,
  isPercentage,
  type,
  description,
  footer,
  id,
  isLow,
  onClick,
  ...rest
}) => (
  <StyledCardReports id={id} type={type} onClick={onClick} {...rest}>
    {type ? (
      <FlexContainer justify="flex-start" align="center" width="100%">
        <FlexContainer className="logo-container">
          {type === ProductType.AWARENESS ? (
            <AwarenessIcon />
          ) : type === ProductType.PHISHING ? (
            <PhishingIcon />
          ) : type === ProductType.SECURITY_CULTURE ? (
            <SecurityCultureIcon />
          ) : null}
        </FlexContainer>
        <h3 className="reports-card-title title">{title}</h3>
      </FlexContainer>
    ) : (
      <h3 className="reports-card-title title">{title}</h3>
    )}
    {isLoading ? (
      <>
        <Skeleton
          className={`reports-card-count ${type ? 'left' : ''}`}
          width="15%"
          height="32px"
        />
      </>
    ) : (
      <>
        <span className={`reports-card-count ${type ? 'left' : ''}`}>
          {isPercentage ? `${count}%` : count}
        </span>
        {description && (
          <span className="reports-card-description">{description}</span>
        )}
      </>
    )}
    {footer && (
      <>
        <Divider />
        <FlexContainer
          className="reports-card-footer"
          justify="flex-start"
          align="center"
          width="100%"
        >
          {isLow ? <DescendingIcon /> : <AscendingIcon />}
          <div className="pl-1">{footer}</div>
        </FlexContainer>
      </>
    )}
  </StyledCardReports>
);

export default CardReports;
